import { useState } from 'react';
import { useGetProviderTags } from '../../api/useGetProviderTags';
import { useCreateProviderTag } from '../../api/useCreateProviderTag';
import { useUpdateProviderTag } from '../../api/useUpdateProviderTag';
import { useDeleteProviderTag } from '../../api/useDeleteProviderTag';
import { ProviderTag } from '../../types';

import { formatProviderTagError } from '../../utils/formatProviderTagError';

export function useManageProviderTagsModal() {
  const { data: tags = [], isLoading } = useGetProviderTags();
  const createTagMutation = useCreateProviderTag();
  const updateTagMutation = useUpdateProviderTag();
  const deleteTagMutation = useDeleteProviderTag();

  const [newTagName, setNewTagName] = useState('');
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagName, setEditingTagName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleCreateTag() {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    setErrorMessage(null);
    try {
      await createTagMutation.mutateAsync(trimmed);
      setNewTagName('');
    } catch (err: unknown) {
      setErrorMessage(formatProviderTagError(err, 'Erreur lors de la création du tag.'));
    }
  }

  function handleStartEdit(tag: ProviderTag) {
    setEditingTagId(tag.id);
    setEditingTagName(tag.name);
    setErrorMessage(null);
  }

  function handleCancelEdit() {
    setEditingTagId(null);
    setEditingTagName('');
  }

  async function handleSaveEdit(tagId: string) {
    const trimmed = editingTagName.trim();
    if (!trimmed) return;
    setErrorMessage(null);
    try {
      await updateTagMutation.mutateAsync({ tagId, name: trimmed });
      setEditingTagId(null);
      setEditingTagName('');
    } catch (err: unknown) {
      setErrorMessage(formatProviderTagError(err, 'Erreur lors de la modification du tag.'));
    }
  }

  async function handleDeleteTag(tagId: string) {
    setErrorMessage(null);
    try {
      await deleteTagMutation.mutateAsync(tagId);
    } catch (err: unknown) {
      setErrorMessage(formatProviderTagError(err, 'Erreur lors de la suppression du tag.'));
    }
  }

  return {
    tags,
    isLoading,
    newTagName,
    setNewTagName,
    editingTagId,
    editingTagName,
    setEditingTagName,
    errorMessage,
    isSubmitting:
      createTagMutation.isPending ||
      updateTagMutation.isPending ||
      deleteTagMutation.isPending,
    handleCreateTag,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteTag,
  };
}

